/**
 * Hedita AppShell - Zinc Design System
 * Persistent shell with Sidebar and Header
 * Pattern: Zinc-950 background, Zinc-900 cards, rounded-xl, shadow-sm
 * Mobile: Floating Menu Button with Bottom Drawer
 */

import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/useAuth";
// import { useLanguage } from "@/lib/language";
// import { usePermissions } from "@/lib/usePermissions";
import { NotificheSheet } from "@/features/comunicazioni";
import { ThemeToggle } from "@/components/ThemeSwitcher";
import { useTheme } from "@/lib/ThemeContext";
import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard,
  Home,
  CalendarDays,
  Users,
  CircleDollarSign,
  Settings,
  LogOut,
  AlertTriangle,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  FileText,
  Star,
  Bell,
  Bed,
  Tag,
  Smartphone,
  UserCheck,
  Link2,
  UserRound,
  MessageCircle,
  CheckCheck,
  CalendarCheck,
  Wrench,
  CreditCard,
  ChevronLeft,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================
// Logo Component
// ============================================================
function HeditaLogo({ size = 36, className }: { size?: number; className?: string }) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 36 36" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect width="36" height="36" rx="9" className="fill-primary" />
      <rect x="7" y="8" width="5.5" height="20" rx="2" fill="white"/>
      <rect x="23.5" y="8" width="5.5" height="20" rx="2" fill="white"/>
      <rect x="7" y="15.5" width="22" height="5" rx="2" fill="white"/>
    </svg>
  );
}

// ============================================================
// Navigation Configuration
// ============================================================
interface NavItem {
  name: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  perm?: string;
}

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  {
    name: "Planner",
    icon: CalendarDays,
    children: [
      { name: "Calendario", href: "/prenotazioni/calendario" },
      { name: "Sincronizzazione canali", href: "/prenotazioni/ical" },
    ],
  },
  {
    name: "Strutture",
    icon: Home,
    children: [
      { name: "Strutture", href: "/strutture/lista" },
      { name: "Camere", href: "/strutture/camere" },
    ],
  },
  { name: "Prenotazioni", href: "/prenotazioni/lista", icon: FileText },
  { name: "Ospiti", href: "/ospiti/database", icon: UserRound },
  { name: "Contratti", href: "/contratti", icon: FileText },
  {
    name: "Guest App",
    icon: Smartphone,
    children: [
      { name: "Anteprima", href: "/guest-app/anteprima" },
      { name: "Configurazione", href: "/guest-app/configurazione" },
    ],
  },
  {
    name: "Team",
    icon: Users,
    children: [
      { name: "Host", href: "/team/host" },
      { name: "Collaboratori", href: "/team/collaboratori" },
    ],
  },
  {
    name: "Finanze",
    icon: CircleDollarSign,
    perm: "finanze",
    children: [
      { name: "Panoramica", href: "/finanze/panoramica" },
      { name: "Entrate", href: "/finanze/entrate" },
      { name: "Uscite", href: "/finanze/uscite" },
      { name: "Transazioni", href: "/finanze/transazioni" },
      { name: "Report", href: "/finanze/report" },
    ],
  },
  {
    name: "Operativo",
    icon: AlertTriangle,
    children: [
      { name: "Attività", href: "/operativo/attivita" },
      { name: "Pulizie", href: "/team/pulizie" },
      { name: "Manutenzione", href: "/team/manutenzione" },
      { name: "Segnalazioni", href: "/operativo/segnalazioni" },
      { name: "Recensioni", href: "/operativo/recensioni" },
    ],
  },
  { name: "Pricing", href: "/pricing", icon: Tag, perm: "pricing" },
  { name: "Impostazioni", href: "/impostazioni", icon: Settings, perm: "impostazioni" },
];

const PERM_MAP: Record<string, string> = {
  "Strutture": "strutture",
  "Prenotazioni": "prenotazioni",
  "Ospiti": "ospiti",
  "Guest App": "guestApp",
  "Team": "team",
  "Finanze": "finanze",
  "Operativo": "operativo",
  "Pricing": "pricing",
  "Impostazioni": "impostazioni",
};

// ============================================================
// Breadcrumbs Component
// ============================================================
function Breadcrumbs() {
  const location = useLocation();
  // const { t } = useLanguage();
  
  const paths = location.pathname.split('/').filter(Boolean);
  
  if (paths.length === 0) return null;
  
  return (
    <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link to="/" className="hover:text-foreground transition-colors">
        Home
      </Link>
      {paths.map((path, index) => (
        <span key={path} className="flex items-center gap-1.5">
          <ChevronRight className="h-3.5 w-3.5" />
          <span className={cn(
            "capitalize",
            index === paths.length - 1 && "text-foreground font-medium"
          )}>
            {path.replace(/-/g, ' ')}
          </span>
        </span>
      ))}
    </nav>
  );
}

// ============================================================
// Notification Bell Component
// ============================================================
type NotificaTipo = 'prenotazione' | 'check-in' | 'recensione' | 'pagamento' | 'manutenzione' | 'segnalazione' | 'contratto' | 'messaggio';

const tipoIcon: Record<NotificaTipo, React.ReactNode> = {
  "prenotazione": <CalendarCheck className="h-4 w-4" />,
  "check-in": <UserCheck className="h-4 w-4" />,
  "recensione": <Star className="h-4 w-4" />,
  "pagamento": <CreditCard className="h-4 w-4" />,
  "manutenzione": <Wrench className="h-4 w-4" />,
  "segnalazione": <AlertTriangle className="h-4 w-4" />,
  "contratto": <FileText className="h-4 w-4" />,
  "messaggio": <MessageCircle className="h-4 w-4" />,
};

const tipoBg: Record<NotificaTipo, string> = {
  "prenotazione": "bg-primary/15 text-primary",
  "check-in": "bg-blue-100 text-blue-600",
  "recensione": "bg-amber-100 text-amber-600",
  "pagamento": "bg-emerald-100 text-emerald-600",
  "manutenzione": "bg-orange-100 text-orange-600",
  "segnalazione": "bg-red-100 text-red-600",
  "contratto": "bg-purple-100 text-purple-600",
  "messaggio": "bg-sky-100 text-sky-600",
};

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifiche, setNotifiche] = useState<Array<{
    id: string;
    tipo: NotificaTipo;
    titolo: string;
    messaggio: string;
    letta: boolean;
    tempo: string;
  }>>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { 
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); 
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const count = notifiche.filter(n => !n.letta).length;

  const segnaLetta = (id: string) => {
    setNotifiche(prev => prev.map(n => n.id === id ? { ...n, letta: true } : n));
  };

  const segnaTutteLette = () => {
    setNotifiche(prev => prev.map(n => ({ ...n, letta: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl hover:bg-accent transition-colors text-muted-foreground"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold bg-primary">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-foreground">Notifiche</span>
            {count > 0 && (
              <button 
                onClick={segnaTutteLette}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Tutte lette
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border">
            {notifiche.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Nessuna notifica</div>
            ) : notifiche.map((n) => (
              <div 
                key={n.id}
                className={cn(
                  "flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-muted/30",
                  !n.letta && "bg-primary/[0.03]"
                )}
                onClick={() => segnaLetta(n.id)}
              >
                <div className="w-1.5 flex-shrink-0 mt-2">
                  {!n.letta && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                </div>
                <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0", tipoBg[n.tipo])}>
                  {tipoIcon[n.tipo]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-xs font-medium leading-snug",
                    n.letta ? "text-muted-foreground" : "text-foreground"
                  )}>{n.titolo}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{n.messaggio}</p>
                  <span className="text-[10px] text-muted-foreground/60 mt-0.5 block">{n.tempo}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// NavItem Component
// ============================================================
function isActiveSection(item: NavItem, pathname: string) {
  if (item.href) return pathname === item.href;
  return item.children?.some((c) => pathname === c.href || pathname.startsWith(c.href || ''));
}

function NavItem({ item, onClose, mobile = false }: { item: NavItem; onClose: () => void; mobile?: boolean }) {
  const location = useLocation();
  // const { t } = useLanguage();
  const active = isActiveSection(item, location.pathname);
  const [open, setOpen] = useState(active);

  const baseLink = cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
    active
      ? mobile 
        ? "bg-primary/25 text-foreground" 
        : "bg-primary/15 text-primary"
      : mobile
        ? "text-muted-foreground hover:bg-muted hover:text-foreground"
        : "text-foreground/75 hover:bg-primary/10 hover:text-primary"
  );

  if (item.href) {
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={baseLink}
      >
        {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
        {item.name}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(baseLink, "w-full text-left justify-between")}
      >
        <span className="flex items-center gap-3">
          {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
          {item.name}
        </span>
        {open
          ? <ChevronDown className="h-4 w-4 opacity-50" />
          : <ChevronRight className="h-4 w-4 opacity-50" />}
      </button>
      {open && (
        <div className="ml-9 mt-0.5 space-y-0.5">
          {item.children?.map((child) => {
            const childActive = location.pathname === child.href;
            return (
              <Link
                key={child.href}
                to={child.href || '#'}
                onClick={onClose}
                className={cn(
                  "block px-3 py-2 rounded-lg text-sm transition-colors font-medium",
                  childActive
                    ? "bg-primary/15 text-primary font-semibold"
                    : "text-foreground/70 hover:bg-primary/10 hover:text-primary"
                )}
              >
                {child.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Mobile NavItem Component
// ============================================================
function MobileNavItem({ item, onClose }: { item: NavItem; onClose: () => void }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const active = isActiveSection(item, location.pathname);

  if (item.href) {
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={cn(
          "flex items-center gap-3 px-3 py-3 rounded-xl transition-colors",
          active 
            ? "bg-primary/15 text-primary font-semibold" 
            : "hover:bg-muted/50 hover:text-primary"
        )}
      >
        <item.icon className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-medium">{item.name}</span>
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex items-center gap-3 px-3 py-3 rounded-xl transition-colors w-full text-left",
          active 
            ? "bg-primary/15 text-primary font-semibold" 
            : "hover:bg-muted/50 hover:text-primary"
        )}
      >
        <item.icon className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-medium">{item.name}</span>
        {open
          ? <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
          : <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />}
      </button>
      {open && (
        <div className="ml-8 mt-1 space-y-1">
          {item.children?.map((child) => {
            const childActive = location.pathname === child.href;
            return (
              <Link
                key={child.href}
                to={child.href || '#'}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors font-medium",
                  childActive
                    ? "bg-primary/15 text-primary font-semibold"
                    : "text-foreground/70 hover:bg-primary/10 hover:text-primary"
                )}
              >
                <span className="text-sm">{child.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main AppShell Component
// ============================================================
export function AppShell() {
  const { currentUser, logout } = useAuth();
  // const { t } = useLanguage();
  // const { canSee } = usePermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const visibleNav = navigation; // Mostra tutti gli elementi temporaneamente

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar - Zinc Dark */}
      <aside 
        className={cn(
          "hidden lg:flex flex-col fixed inset-y-0 left-0 z-40",
          "bg-card border-r border-border",
          "transition-all duration-300 ease-in-out",
          sidebarCollapsed ? "w-16" : "w-64"
        )}
      >
        {/* Logo */}
        <div className={cn(
          "flex items-center gap-2.5 px-4 pt-5 pb-4 border-b border-border",
          sidebarCollapsed && "justify-center px-2"
        )}>
          <HeditaLogo size={sidebarCollapsed ? 32 : 36} />
          {!sidebarCollapsed && (
            <div>
              <div className="text-base font-bold text-foreground leading-tight tracking-tight">Hedita</div>
              <div className="text-[11px] text-muted-foreground">Gestione strutture</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-3 space-y-0.5">
          {visibleNav.map((item) => (
            <NavItem 
              key={item.name} 
              item={{ ...item, name: sidebarCollapsed && item.children ? item.name.slice(0, 1) : item.name }} 
              onClose={() => {}} 
            />
          ))}
        </nav>

        {/* Footer */}
        <div className={cn(
          "px-4 py-4 border-t border-border",
          sidebarCollapsed && "px-2"
        )}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn(
              "flex items-center gap-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors",
              sidebarCollapsed && "justify-center"
            )}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span>Collassa</span>
              </>
            )}
          </button>
          
          {!sidebarCollapsed && (
            <>
              <div className="flex items-center gap-2.5 mb-3 mt-3">
                <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                  {currentUser?.email?.[0]?.toUpperCase() ?? '?'}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-foreground truncate">
                    {currentUser?.email?.split('@')[0]}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {currentUser?.email}
                  </div>
                </div>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Esci
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Mobile Menu Button - Bottom Right */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all"
        aria-label="Menu"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Main Content Area */}
      <main className={cn(
        "flex-1 min-w-0 transition-all duration-300 bg-background",
        "lg:ml-64"
      )}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            {/* Left: Breadcrumbs */}
            <div className="flex items-center gap-3">
              <Breadcrumbs />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6 pb-24 lg:pb-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </div>

        {/* Mobile Menu Drawer - Full List */}
        <div className={cn(
          "fixed inset-0 z-50 lg:hidden",
          mobileMenuOpen ? "pointer-events-auto" : "pointer-events-none"
        )}>
          {/* Backdrop */}
          <div 
            className={cn(
              "absolute inset-0 bg-black/60 transition-opacity",
              mobileMenuOpen ? "opacity-100" : "opacity-0"
            )}
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Bottom Drawer */}
          <div className={cn(
            "absolute bottom-0 left-0 right-0 bg-card border-t border-border rounded-t-3xl shadow-2xl transition-transform duration-300 max-h-[80vh] flex flex-col",
            mobileMenuOpen ? "translate-y-0" : "translate-y-full"
          )}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 rounded-full bg-muted" />
            </div>
            
            {/* Header */}
            <div className="px-4 pb-2 border-b border-border">
              <div className="flex items-center gap-3">
                <HeditaLogo size={28} />
                <span className="font-bold text-foreground">Hedita Menu</span>
              </div>
            </div>
            
            {/* Menu Items - Vertical List with Icons */}
            <nav className="px-2 py-3 overflow-y-auto">
              <div className="space-y-1">
                {visibleNav.map((item) => (
                  <MobileNavItem 
                    key={item.name} 
                    item={item} 
                    onClose={() => setMobileMenuOpen(false)} 
                  />
                ))}
              </div>
            </nav>
            
            {/* Footer */}
            <div className="px-4 py-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                    {currentUser?.email?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                    {currentUser?.email}
                  </div>
                </div>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Esci
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AppShell;
